import axios from 'axios';
import {
    getIsAuthenticated,
    getIsAuthView,
    getJWT,
    setIsAuthenticated,
    setPreAuthPath
} from '@/services/auth.ts'

const http = axios.create({
    baseURL: `//${window.location.host}/api`,
    withCredentials: true,
    headers: {
        'STATUSHUB-HUB-API-CLIENT': 'true',
        'HUB-HOST': window.location.host,
        'LOCALE': 'en-US'
    }
});

let refreshTokenPromise;
const AUTH_FAIL_COUNT_KEY = 'auth-fail-count'
const AUTH_FAIL_COUNT_THRESHOLD = 5
const BASE_URL_KEY = 'base-url'

const getSafeBaseUrl = (rawValue) => {
    if (!rawValue) {
        return null
    }

    try {
        const parsed = new URL(rawValue, window.location.origin)
        return parsed.origin === window.location.origin
            ? parsed.origin
            : null
    } catch (_) {
        return null
    }
}

const setSafeBaseUrl = (rawValue) => {
    const safeUrl = getSafeBaseUrl(rawValue)

    if (safeUrl) {
        localStorage.setItem(BASE_URL_KEY, safeUrl)
    } else {
        localStorage.removeItem(BASE_URL_KEY)
    }

    return safeUrl
}

const getBaseUrl = () => {
    return localStorage.getItem(BASE_URL_KEY) || window.location.origin
}

const redirectToLogin = () => {
    setIsAuthenticated(false)
    setPreAuthPath(window.location.pathname + window.location.search)
    const url = getBaseUrl()
    window.location.replace(url + '/jwt/token');
}

const refreshToken = () => {
    if (refreshTokenPromise) { return refreshTokenPromise }
    refreshTokenPromise = refreshTokenAction();
    return refreshTokenPromise;
}

const refreshTokenAction = async () => {
    try {
        const { data } = await http.get('jwt/token');

        refreshTokenPromise = undefined;

        // hack
        if (data.token) {
            setIsAuthenticated(true, data.token)
        } else {
            throw new Error()
        }
    } catch (_) {
        refreshTokenPromise = undefined;

        if (!getIsAuthView()) {
            redirectToLogin()
        }
        throw new Error('authorization-required')
    }
}

http.interceptors.request.use(config => {
    const jwt = getJWT();
    const timezone = $cookies.get('timezone');

    const url = getBaseUrl()
    config.url = url + '/api/' + config.url

    if (timezone) {
        config.headers['TIMEZONE'] = timezone;
    }

    if (jwt) {
        config.headers['Authorization'] = `Bearer ${jwt}`;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

http.interceptors.response.use(
    (response) => {
        if (localStorage.getItem(AUTH_FAIL_COUNT_KEY) &&
            response.config.url.includes('/config')) {
            localStorage.removeItem(AUTH_FAIL_COUNT_KEY)
        }

        const jwt = getJWT()
        if (getIsAuthenticated() === false && jwt) {
            setIsAuthenticated(true, jwt)
        }

        return response;
    },
    async (error) => {
        if (error.response && error.response.status === 401) {
            setSafeBaseUrl(error.response?.data?.base_url)

            let authFailCount =
                parseInt(localStorage.getItem(AUTH_FAIL_COUNT_KEY)) || 0
            if (authFailCount < AUTH_FAIL_COUNT_THRESHOLD) {
                authFailCount++
                localStorage
                    .setItem(AUTH_FAIL_COUNT_KEY, authFailCount.toString())
            } else {
                localStorage.removeItem(AUTH_FAIL_COUNT_KEY)
                // TODO: Convert to custom error
                return Promise.reject(new Error('unable-to-authorize'))
            }

            try {
                await refreshToken()
                return http.request({
                    ...error.config,
                    url: error.config.url.split('/api/').pop()
                })
            } catch (e) {
                return Promise.reject(e)
            }
        }

        return Promise.reject(error)
    }
)

export default http;
